import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Loading } from "@/components/common";
import Login from "@/pages/Login";

const P = {
  Dashboard: lazy(() => import("@/pages/Dashboard")),
  Companies: lazy(() => import("@/pages/Companies")),
  DomainSettings: lazy(() => import("@/pages/DomainSettings")),
  Employees: lazy(() => import("@/pages/Employees")),
  Team: lazy(() => import("@/pages/Team")),
  Attendance: lazy(() => import("@/pages/Attendance")),
  Leaves: lazy(() => import("@/pages/Leaves")),
  LeaveBalances: lazy(() => import("@/pages/LeaveBalances")),
  Holidays: lazy(() => import("@/pages/Holidays")),
  Timesheet: lazy(() => import("@/pages/Timesheet")),
  Payroll: lazy(() => import("@/pages/Payroll")),
  MyPayroll: lazy(() => import("@/pages/MyPayroll")),
  Documents: lazy(() => import("@/pages/Documents")),
  Recruitment: lazy(() => import("@/pages/Recruitment")),
  Onboarding: lazy(() => import("@/pages/Onboarding")),
  Performance: lazy(() => import("@/pages/Performance")),
  Offboarding: lazy(() => import("@/pages/Offboarding")),
  Engagement: lazy(() => import("@/pages/Engagement")),
  Helpdesk: lazy(() => import("@/pages/Helpdesk")),
  Assistant: lazy(() => import("@/pages/Assistant")),
  Reports: lazy(() => import("@/pages/Reports")),
  Profile: lazy(() => import("@/pages/Profile")),
  Branding: lazy(() => import("@/pages/Branding")),
  AuditLog: lazy(() => import("@/pages/AuditLog")),
  LeavePolicy: lazy(() => import("@/pages/LeavePolicy")),
  LeaveCalendar: lazy(() => import("@/pages/LeaveCalendar")),
  OrgChart: lazy(() => import("@/pages/OrgChart")),
};

const ROUTES = [
  ["/", "Dashboard"], ["/companies", "Companies", ["super_admin"]], ["/domain-settings", "DomainSettings", ["super_admin"]],
  ["/employees", "Employees", ["admin", "super_admin"]], ["/team", "Team", ["manager"]], ["/attendance", "Attendance"], ["/leaves", "Leaves"],
  ["/holidays", "Holidays"], ["/timesheet", "Timesheet"], ["/payroll", "Payroll", ["admin", "super_admin"]], ["/my-payroll", "MyPayroll"],
  ["/documents", "Documents"], ["/recruitment", "Recruitment", ["admin", "super_admin"]], ["/onboarding", "Onboarding"],
  ["/performance", "Performance"], ["/offboarding", "Offboarding"], ["/engagement", "Engagement"], ["/helpdesk", "Helpdesk"],
  ["/assistant", "Assistant"], ["/reports", "Reports", ["admin", "super_admin", "manager"]], ["/profile", "Profile"], ["/branding", "Branding", ["admin"]],
  ["/audit-log", "AuditLog", ["admin", "super_admin"]], ["/leave-policy", "LeavePolicy", ["admin"]], ["/leave-balances", "LeaveBalances", ["admin"]], ["/leave-calendar", "LeaveCalendar", ["admin", "manager"]], ["/org-chart", "OrgChart", ["admin", "manager"]],
];

function Protected({ children, roles }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) {
    const code = localStorage.getItem("portal_code");
    return <Navigate to={code ? `/login/${code}` : "/login"} replace />;
  }
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function LoginRoute() {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  return user ? <Navigate to="/" replace /> : <Login />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route path="/login" element={<LoginRoute />} />
            <Route path="/login/:code" element={<LoginRoute />} />
            <Route element={<Protected><Layout /></Protected>}>
              {ROUTES.map(([path, name, roles]) => {
                const C = P[name];
                return <Route key={path} path={path} element={<Protected roles={roles}><C /></Protected>} />;
              })}
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}
